/* @flow */

import type { GamestreamGameConfig } from './types';

import GameManager from './gameManager';

/* eslint-disable no-magic-numbers */
const DEFAULT_GAMEPAD_CONFIG = {
  GamepadGrid: [16, 10],
  lBtA: { xy: [-3, -4] },
  lBtB: { xy: [-2, -5] },
  lBtEnter: { xy: [3, 0] },
  lBtEsc: { xy: [0, 0] },
  lBtL: { xy: [-11, -5] },
  lBtL3: { xy: [-10, -2] },
  lBtLTr: { xy: [4, -4] },
  lBtR: { xy: [-6, -5] },
  lBtR3: { xy: [-7, -2] },
  lBtRTr: { xy: [-6, -4] },
  lBtSelect: { xy: [1, -10] },
  lBtStart: { xy: [2, -10] },
  lBtX: { xy: [-4, -5] },
  lBtY: { xy: [-3, -6] },
  lPadD: { xy: [1, -5] },
  lPadL: { xy: [4, -3] },
  lPadR: { xy: [-6, -3] },
};
/* eslint-enable no-magic-numbers */

class Player {
  static getVirtualGamePadConfig(sgxConfig: GamestreamGameConfig): { virtualGamePadConfig: any, virtualPadAssetsPath: string } {
    let virtualPadAssetsPath: string = 'default';
    const assets = sgxConfig['virtualpad-assets'] ? sgxConfig['virtualpad-assets'].toLowerCase() : null;
    if (typeof assets === 'string' && ['ps4', 'xboxone'].includes(assets)) {
      virtualPadAssetsPath = assets;
    }

    let virtualGamePadConfig: any = undefined;
    const virtualPadConfigs = sgxConfig['virtualpad-configs'];

    if (virtualPadConfigs !== undefined) {
      const layoutName: string = sgxConfig['default-virtualpad'] || 'normal';
      virtualGamePadConfig = virtualPadConfigs[layoutName] || virtualPadConfigs.default;
    }

    if (virtualGamePadConfig === undefined) {
      virtualGamePadConfig = sgxConfig['virtualpad-config-touchscreen'] || sgxConfig['virtualpad-config-normal'];
    }

    return {
      virtualGamePadConfig,
      virtualPadAssetsPath,
    };
  }

  static instantiateClient(container: HTMLElement, sgxConfig: GamestreamGameConfig, useSsl: boolean, showSplashScreen: boolean): CGXClient {
    const config = Player.getVirtualGamePadConfig(sgxConfig);

    // Override optimized virtual gamepad if requested (e.g. JIO)
    if (sgxConfig['force-default-gamepad']) {
      config.virtualGamePadConfig = DEFAULT_GAMEPAD_CONFIG;
      config.virtualPadAssetsPath = 'default';
    }

    const CGXClient = GameManager.CGXClientClass;

    return new CGXClient({
      audio_port: sgxConfig['audio-port'],
      auth_key: sgxConfig['enc'],
      bitrate: parseInt(sgxConfig['bitrate'], 10),
      container: container,
      force_rtc_tcp: sgxConfig['network-is-wifi'] === 'on',
      height: sgxConfig['height'],
      host: sgxConfig['ip'],
      input_port: sgxConfig['input-port'],
      keyboard_layout: 'azerty',
      micro_port: sgxConfig['webcam-audio-port'],
      mouse_local: sgxConfig['mouse-local'] === 'on',
      mouse_relative: true,
      secured: useSsl,
      splash_screen: showSplashScreen ? '/images/splashscreen.png' : null,
      toolbar_default_hidden: false,
      toolbar_force_display_mode: 'hidden', // Available modes: hover, toggle, lock, hidden
      use_gamepad: true,
      use_input: true,
      use_keyboard: true,
      use_microphone: false,
      use_mouse: true,
      use_virtual_gamepad: sgxConfig['default-virtualpad'] !== '',
      use_webcam: false,
      video_port: sgxConfig['video-port'],
      virtual_gamepad_config: config.virtualGamePadConfig,
      virtualpad_assets_path: `/images/virtualpad/${config.virtualPadAssetsPath}`,
      virtualpad_kind: 'local',
      webcam_port: sgxConfig['webcam-video-port'],
      width: sgxConfig['width'],
    });
  }
}

export default Player;
